import { Skeleton } from 'primereact/skeleton';
import { ResponsiveContainer, PieChart, Pie, Legend } from 'recharts';
import { formatCurrency } from '../money';
import { Investor } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { useMemo } from 'react';

// Finance-inspired colors from CSS variables
const CHART_COLORS = [
  'var(--chart-color-profit)', // Green (profit)
  'var(--chart-color-stable)', // Blue (stable)
  'var(--chart-color-caution)', // Amber (caution)
  'var(--chart-color-luxury)', // Purple (luxury)
  'var(--chart-color-tech)', // Cyan (tech)
  'var(--chart-color-energy)', // Orange (energy)
  'var(--chart-color-growth)', // Light Green (growth)
  'var(--chart-color-conservative)', // Indigo (conservative)
  'var(--chart-color-disruptive)', // Pink (disruptive)
  'var(--chart-color-loss)', // Red (loss)
];

const renderCurrencyLabel = (data: Investor) => {
  return formatCurrency(data.currency ?? 'EUR', data.valueLocal);
};

type PCompanyInvestorPieChartProps = {
  data: Investor[];
  isLoading: boolean;
  error: Error | null;
};

const style = {
  top: '50%',
  right: 55,
  transform: 'translate(0, -50%)',
  lineHeight: '24px',
};

export default function PCompanyInvestorPieChart({
  data,
  isLoading,
  error,
}: PCompanyInvestorPieChartProps) {
  if (isLoading) {
    return (
      <div className="h-full">
        <Skeleton className="h-full"></Skeleton>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 flex flex-column h-full justify-content-center align-items-center">
        <div className="flex w-full h-2rem">
          <h3 className="text-lg font-semibold mb-0">Co-Investors</h3>
        </div>
        <div className="align-items-center flex h-full p-error mb-6">
          Something went wrong.
        </div>
      </div>
    );
  }

  const dataWithColors = useMemo(
    () =>
      data.map((investor, index) => ({
        ...investor,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      })),
    [data],
  );

  if (
    !Array.isArray(dataWithColors) ||
    !dataWithColors.length ||
    dataWithColors.every((entry: Investor) => entry.valueLocal === 0)
  ) {
    return (
      <div className="p-3 flex flex-column h-full justify-content-center align-items-center">
        <div className="flex w-full h-2rem">
          <h3 className="text-lg font-semibold mb-0">Co-Investors</h3>
        </div>
        <div className="align-items-center flex h-full  mb-6">
          No data found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <h3 className="text-lg font-semibold mb-4">Co-Investors</h3>
      <div className="h-20rem">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            width={400}
            height={400}
            className="w-full"
            margin={{ top: 5, right: 250, bottom: 5, left: 5 }}
          >
            <Legend
              payload={dataWithColors.map((v) => ({
                value: `${v.name} (${v.type})`,
                color: v.fill,
              }))}
              iconSize={10}
              layout="vertical"
              verticalAlign="middle"
              wrapperStyle={style}
            />
            <Pie
              data={dataWithColors}
              dataKey="valueLocal"
              cx="50%"
              cy="50%"
              innerRadius={50}
              fill="#82ca9d"
              label={renderCurrencyLabel}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
